import React, { useState, useEffect } from "react";
import { Window } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useDispatch } from "react-redux";
import { getUserCredential } from "../../../lib/cookies";
import { generateHeader } from "src/api-setup/api-helper";
import { POSTAPI } from "src/api-setup/api-helper";
import { GET_TIMESHEET_DETAILS } from "src/api-setup/api-endpoints";
import { setNewMessage } from "../../../redux/actions/setting-actions";
import { downloadFile } from "../services/downloadFile";
import { getTimesheetProgressDetails } from "../services/getTimesheetProgressDetails";
import StepProgressBar, { mapApiDataToStepDetails } from "src/components/StepProgressBar";
import FileViewerDialog from "./FileViewerDialog";
import { LoadingPanel } from "src/components/loadingPanel";
import "./ViewPayRequestDialog.scss";
import ErrorDisplay from "src/components/error-display/ErrorDisplay";
import { getUserTimezone } from "../utils/dateUtils";
import { formatTimesheetQuantity } from "../utils/thresholdQuantity";
import { convertTimeZone } from "src/lib/date-time.util";

const ViewPayRequestDialog = ({ visible, onClose, providerId, rowData }) => {
  const dispatch = useDispatch();
  const cookiesData = getUserCredential();

  const [timesheetData, setTimesheetData] = useState(null);
  const [progressDetails, setProgressDetails] = useState([]);
  const [isTimesheetLoading, setIsTimesheetLoading] = useState(false);
  const [isNotFoundError, setIsNotFoundError] = useState(false);
  const [fileViewerVisible, setFileViewerVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({});


  const fetchTimesheetDetails = async () => {
    if (!cookiesData?.secretToken || !rowData.timesheet_mstr_key) return;
    setIsTimesheetLoading(true);

    try {
      const apiHeaders = generateHeader();
      const requestBody = {
        in_tenant_id: cookiesData.out_tenant_id,
        in_timesheet_mstr_key: rowData.timesheet_mstr_key,
        in_user_key: cookiesData.out_user_id,
        in_user_type: "PROVIDER",
        in_filter: null,
        in_sort_number: 1,
        in_sort_order: "ASC",
      };

      const response = await POSTAPI(false, GET_TIMESHEET_DETAILS, null, apiHeaders, requestBody, dispatch);

      if (response?.data?.row_result && response.data.row_result.length > 0) {
        const timesheetData = response.data.row_result[0];
        console.log("FETCH TIMESHEET DETAILS: timesheetData:", { timesheetData });
        setTimesheetData(timesheetData);
        setIsTimesheetLoading(false);
        setIsNotFoundError(false);
      } else {
        setIsNotFoundError(true);
        dispatch(
          setNewMessage({
            message: "No timesheet data found",
            type: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error fetching timesheet details:", error);
      setIsNotFoundError(true);
      dispatch(
        setNewMessage({
          message: "Error fetching timesheet details",
          type: "error",
        })
      );
      setIsTimesheetLoading(false);
    }
  };

  const fetchTimesheetProgressDetails = async () => {
    if (!cookiesData?.secretToken || !rowData.timesheet_mstr_key) return;

    try {
      const progressData = await getTimesheetProgressDetails(cookiesData, dispatch, rowData.timesheet_mstr_key);

      // Transform the API data to step details format
      const transformedSteps = mapApiDataToStepDetails(progressData);
      setProgressDetails(transformedSteps);
    } catch (error) {
      console.error("Error fetching timesheet progress details:", error);
      dispatch(
        setNewMessage({
          message: "Error fetching progress details",
          type: "error",
        })
      );
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    return (
      convertTimeZone({
        date,
        convertTimeZone: getUserTimezone(),
        outputFormat: "MM/dd/yyyy",
      }) || ""
    );
  };

  // Utility function to format budget with 3 decimal places
  const formatBudget = (budget) => {
    if (budget === null || budget === undefined) return "0.00";
    return Number(budget).toFixed(2);
  };

  // Utility function to format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Utility function to get file icon based on MIME type
  const getFileIcon = (mimeType) => {
    if (!mimeType) return "fa-file";

    const type = mimeType.toLowerCase();
    if (type.startsWith("image/")) return "fa-file-image";
    if (type === "application/pdf") return "fa-file-pdf";
    if (type.includes("word") || type.includes("document")) return "fa-file-word";
    if (type.includes("excel") || type.includes("spreadsheet")) return "fa-file-excel";
    if (type.includes("text")) return "fa-file-alt";
    return "fa-file";
  };

  // Handle file view
  const handleFileView = (file) => {
    setSelectedFile(file);
    setFileViewerVisible(true);
  };

  // Handle file download
  const handleFileDownload = async (file) => {
    try {
      setDownloadProgress((prev) => ({ ...prev, [file.document_name]: 0 }));

      await downloadFile(file, (percentage) => {
        setDownloadProgress((prev) => ({ ...prev, [file.document_name]: percentage }));
      });

      // Clear progress after a delay
      setTimeout(() => {
        setDownloadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[file.document_name];
          return newProgress;
        });
      }, 1000);
    } catch (error) {
      console.error("Error downloading file:", error);
      dispatch(
        setNewMessage({
          message: "Failed to download file",
          type: "error",
        })
      );
      setDownloadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[file.document_name];
        return newProgress;
      });
    }
  };

  useEffect(() => {
    fetchTimesheetDetails();
    fetchTimesheetProgressDetails();
  }, [visible, rowData]);

  const handleClose = () => {
    onClose();
  };

  const ModalTitle = () => {
    return (
      <div className="modal-header">
        <div className="modal-title-container">
          <span className="modal-title">VIEW PAY REQUEST</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <Window
        key={`window-${visible}`}
        visible={visible}
        onClose={handleClose}
        title={<ModalTitle />}
        width={700}
        height={650}
        className="view-pay-request-dialog"
        modal={true}
        closeIcon={true}
        backdrop={true}
      >
        {isNotFoundError ? (
          <ErrorDisplay 
            icon="fa-exclamation-circle"
            title="No Pay Request Found"
            description="The pay request you are looking for does not exist."
          />
        ) : isTimesheetLoading ? (
          <LoadingPanel gridId="view-pay-request-dialog" />
        ) : (
          <div className="dialog-content">
            {progressDetails && progressDetails.length > 0 && (
              <div className="view-pay-request-progress-container">
                <StepProgressBar steps={progressDetails} localTimezone={timesheetData?.local_timezone} />
              </div>
            )}
            {(timesheetData?.thresold_unit_type === "hours" || timesheetData?.thresold_unit_type==="days") && (timesheetData?.is_soft_flagged === 1 || timesheetData?.is_hard_flagged === 1) ? (
              <div className="view-pay-request-warning-container">
                <i className="fas fa-exclamation-triangle"></i>
                <span>Entered hours are above contract limit.</span>
              </div>
            ) : null}
            <div className="view-pay-request-container border-container">
              <div className="view-pay-request-provider">
                <div className="view-pay-request-provider-icon">
                  <i className="fas fa-user"></i>
                </div>
                <div className="view-pay-request-provider-container">
                  <div className="field-label">Provider</div>
                  <div className="field-value">{timesheetData?.provider_name}</div>
                </div>
              </div>
            </div>
            <div className="view-pay-request-container">
              <div className="view-pay-request-location border-container">
                <div className="field-label">Location</div>
                <div className="field-value">{timesheetData?.location_name}</div>
              </div>
              <div className="view-pay-request-specialty border-container">
                <div className="field-label">Specialty</div>
                <div className="field-value">{timesheetData?.specialty_name}</div>
              </div>
            </div>
            <div className="view-pay-request-container">
              <div className="view-pay-request-type border-container">
                <div className="field-label">Pay Request Type</div>
                <div className="field-value">{timesheetData?.contract_type}</div>
              </div>
              <div className="view-pay-request-name border-container">
                <div className="field-label">Pay Request Name</div>
                <div className="field-value">{timesheetData?.contract_name}</div>
              </div>
            </div>
            <div className="view-pay-request-container">
              <div className="view-pay-request-hours border-container">
                <div className="field-label">Total Quantity</div>
                <div className="field-value">{formatTimesheetQuantity(timesheetData, { capitalizeUnit: true })}</div>
              </div>
              <div className="view-pay-request-budget border-container">
                <div className="field-label">Total Amount</div>
                <div className="field-value">$ {formatBudget(timesheetData?.budget)}</div>
              </div>
            </div>
            {timesheetData?.rate_type === "step" ||
            timesheetData?.rate_type === "multisite" ||
            timesheetData?.rate_type === "cliff" ? (
              <div className="view-pay-request-warning-container">
                <i className="fas fa-exclamation-triangle"></i>
                <span>
                  The payment amount is estimated and may be adjusted once all pay requests for this period are
                  submitted and processed.
                </span>
              </div>
            ) : null}
            <div className="view-pay-request-container">
              <div className="view-pay-request-service-date border-container">
                <div className="field-label">Service Date</div>
                <div className="field-value">
                  {timesheetData?.service_date ? formatDate(timesheetData.service_date) : ""}
                </div>
              </div>
              <div className="view-pay-request-post-date border-container">
                <div className="field-label">Post Date</div>
                <div className="field-value">{timesheetData?.post_date ? formatDate(timesheetData.post_date) : ""}</div>
              </div>
            </div>
            <div className="view-pay-request-container border-container">
              <div className="view-pay-request-description">
                <div className="field-label">Description</div>
                <div className="field-value" style={{ color: timesheetData?.ts_description ? "" : "#999999" }}>
                  {timesheetData?.ts_description ? timesheetData?.ts_description : "No description"}
                </div>
              </div>
            </div>
            {timesheetData?.comments &&
              timesheetData.comments.filter((comment) => comment.comment && comment.comment.trim() !== "").length >
                0 && (
                <div className="view-pay-request-container border-container">
                  <div className="view-pay-request-comments">
                    <div className="field-label">Approver Comments</div>
                    <div className="comments-list">
                      {timesheetData.comments
                        .filter((comment) => comment.comment && comment.comment.trim() !== "")
                        .map((comment, index) => (
                          <div key={index} className="comment-item">
                            <div className="comment-approver">
                              <i className="fas fa-user-circle comment-icon"></i>
                              <span className="approver-name">{comment.approver || "Unknown Approver"}</span>
                            </div>
                            <div className="comment-text">{comment.comment}</div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
            {/* {timesheetData?.documents && timesheetData.documents.length > 0 && ( */}
            <div className="view-pay-request-container border-container">
              <div className="view-pay-request-documents">
                <div className="field-label">Attachments</div>
                <div className="documents-list">
                  {timesheetData?.documents?.length > 0 ? (
                    timesheetData.documents.map((doc, index) => (
                      <div key={index} className="document-item">
                        <div className="document-info">
                          <i className={`fas ${getFileIcon(doc.document_type)} document-icon`}></i>
                          <div className="document-details">
                            <div className="document-name">{doc.original_name}</div>
                            <div className="document-size">{formatFileSize(doc.document_size)}</div>
                          </div>
                        </div>
                        <div className="document-actions">
                          <Tooltip anchorElement="target" position="top" openDelay={100}>
                            <Button
                              className="document-action-btn view-btn"
                              onClick={() => handleFileView(doc)}
                              title="View file"
                            >
                              <i className="fas fa-eye"></i>
                            </Button>
                          </Tooltip>
                          <Tooltip anchorElement="target" position="top" openDelay={100}>
                            <Button
                              className="document-action-btn download-btn"
                              onClick={() => handleFileDownload(doc)}
                              title="Download file"
                              disabled={
                                downloadProgress[doc.document_name] !== undefined &&
                                downloadProgress[doc.document_name] < 100
                              }
                            >
                              {downloadProgress[doc.document_name] !== undefined &&
                              downloadProgress[doc.document_name] < 100 ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className="fas fa-download"></i>
                              )}
                            </Button>
                          </Tooltip>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: "#999999", fontSize: "12px" }}>
                      <span>No attachments</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* )} */}
          </div>
        )}
      </Window>
      {fileViewerVisible && (
        <FileViewerDialog
          visible={fileViewerVisible}
          onClose={() => {
            setFileViewerVisible(false);
            setSelectedFile(null);
          }}
          file={selectedFile}
        />
      )}
    </>
  );
};

export default ViewPayRequestDialog;
